import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  createClub,
  updateClub,
  deleteClub,
  getClubMembers,
  addClubMember,
  removeClubMember,
  type Club,
  type ClubMember,
} from "../../services/clubs";
import { getClubs } from "../../services/api";
import { getUsers } from "../../services/users";
import { getClubPlans, type PlanDefinition } from "../../services/plans";
import Pagination, { usePagination } from "../../components/Pagination";
import ImageUpload from "../../components/ImageUpload";
import LoginAsButton from "../../components/LoginAsButton";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import { formatDate } from "../../lib/dateFormat";

type ClubAdmin = {
  id: string;
  email: string;
  full_name: string | null;
  status: "active" | "demo" | "inactive";
  demo_started_at: string | null;
  demo_expires_at: string | null;
};

type ClubWithRole = {
  club: Club & { club_admin?: ClubAdmin | null };
  role: string;
};

export default function ClubManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [clubs, setClubs] = useState<ClubWithRole[]>([]);
  const [selectedClub, setSelectedClub] = useState<Club | null>(null);
  const [clubMembers, setClubMembers] = useState<ClubMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // With dozens of clubs, a wall of cards is a scrolling exercise. The table
  // is the default: one row per club, every number the platform asks about
  // visible at a glance, and sortable by the column you came to compare.
  const [view, setView] = useState<"table" | "cards">("table");
  const [planFilter, setPlanFilter] = useState("");
  const [clubPlans, setClubPlans] = useState<PlanDefinition[]>([]);
  const [cityFilter, setCityFilter] = useState("");
  const [adminFilter, setAdminFilter] = useState<"" | "with" | "without">("");
  const [sortBy, setSortBy] = useState<"name" | "city" | "coaches" | "athletes" | "sports" | "plan" | "created">("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const adminOf = (c: any) => c.club_admin || null;

  // Every distinct city and plan actually present, so the filters only ever
  // offer something that will return rows.
  const cities = React.useMemo(
    () => Array.from(new Set(clubs.map(({ club }) => club.city).filter(Boolean) as string[])).sort(
      (a, b) => a.localeCompare(b, "ro")
    ),
    [clubs]
  );
  // Opțiunile din filtrul de planuri vin din planurile definite în
  // /admin/club-plans, nu din codurile găsite în datele cluburilor. Altfel
  // lista adună orice cod vechi rămas pe un club și devine de necitit.
  //
  // Dacă totuși există cluburi pe un cod care nu mai e un plan definit, acel
  // cod apare la final, separat: ascunzându-l, cluburile respective n-ar mai
  // putea fi găsite prin filtru.
  const definedPlans = React.useMemo(
    () => clubPlans.filter((p) => p.is_active).sort((a, b) => a.sort_order - b.sort_order),
    [clubPlans]
  );
  const legacyPlanCodes = React.useMemo(() => {
    if (clubPlans.length === 0) return []; // planurile încă nu s-au încărcat
    const known = new Set(definedPlans.map((p) => p.code));
    return Array.from(
      new Set(clubs.map(({ club }) => club.plan_code).filter((c): c is string => !!c && !known.has(c)))
    ).sort();
  }, [clubs, clubPlans, definedPlans]);

  const filteredClubs = React.useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const rows = clubs.filter(({ club }) => {
      const admin = adminOf(club);
      // Search covers the club, its city and its admin — the three things
      // somebody has in mind when they go looking for a club.
      const haystack = [club.name, club.city, admin?.full_name, admin?.email]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (q && !haystack.includes(q)) return false;
      if (planFilter && club.plan_code !== planFilter) return false;
      if (cityFilter && club.city !== cityFilter) return false;
      if (adminFilter === "with" && !admin) return false;
      if (adminFilter === "without" && admin) return false;
      return true;
    });

    const dir = sortDir === "asc" ? 1 : -1;
    const key = (c: any) => {
      switch (sortBy) {
        case "city": return (c.city || "").toLowerCase();
        case "coaches": return c.coach_count || 0;
        case "athletes": return c.member_count || 0;
        case "sports": return c.sports_count || 0;
        case "plan": return (c.plan_code || "").toLowerCase();
        case "created": return new Date(c.created_at).getTime();
        default: return (c.name || "").toLowerCase();
      }
    };
    return [...rows].sort((a, b) => {
      const ka = key(a.club), kb = key(b.club);
      if (typeof ka === "number" && typeof kb === "number") return (ka - kb) * dir;
      return String(ka).localeCompare(String(kb), "ro") * dir;
    });
  }, [clubs, searchTerm, planFilter, cityFilter, adminFilter, sortBy, sortDir]);

  // Cluburile vin întregi de la server, deci paginăm în client. Aceeași felie
  // alimentează ambele vederi (tabel și carduri), ca numărul paginii să nu
  // sară la comutare.
  const { page, setPage, pageSize, total, visible: pagedClubs } = usePagination(filteredClubs);

  const toggleSort = (col: typeof sortBy) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      // Counts are most useful biggest-first; names and cities alphabetically.
      setSortDir(["coaches", "athletes", "sports", "created"].includes(col) ? "desc" : "asc");
    }
  };

  const hasFilters = !!(searchTerm || planFilter || cityFilter || adminFilter);
  const clearFilters = () => {
    setSearchTerm("");
    setPlanFilter("");
    setCityFilter("");
    setAdminFilter("");
  };

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    logo_url: "",
    description: "",
    physical_address: "",
    plan_code: "demo",
  });

  const [memberFormData, setMemberFormData] = useState({
    user_id: "",
    role_code: "athlete",
  });

  const [availableUsers, setAvailableUsers] = useState<any[]>([]);

  useEffect(() => {
    fetchClubs();
    // Planurile de club alimentează filtrul de planuri. Dacă apelul cade,
    // filtrul rămâne pe „Toate planurile" și tabelul e neatins.
    getClubPlans()
      .then(setClubPlans)
      .catch((e) => console.error("Failed to fetch club plans:", e));
  }, []);

  const fetchClubs = async () => {
    setLoading(true);
    try {
      const data = await getClubs();
      setClubs(data);
    } catch (error) {
      console.error("Failed to fetch clubs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClubMembers = async (clubId: string) => {
    try {
      const members = await getClubMembers(clubId);
      setClubMembers(members);
    } catch (error) {
      console.error("Failed to fetch club members:", error);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const data = await getUsers({ pageSize: 100 });
      setAvailableUsers(data.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const handleCreateClub = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createClub(formData);
      toast.success("Club creat cu succes!");
      setShowCreateModal(false);
      resetForm();
      fetchClubs();
    } catch (error: any) {
      console.error("Failed to create club:", error);
      toast.error(error.response?.data?.error || "Nu s-a putut crea clubul");
    }
  };

  const handleUpdateClub = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClub) return;
    try {
      await updateClub(selectedClub.id, formData);
      toast.success("Club actualizat cu succes!");
      setShowEditModal(false);
      setSelectedClub(null);
      resetForm();
      fetchClubs();
    } catch (error: any) {
      console.error("Failed to update club:", error);
      toast.error(error.response?.data?.error || "Nu s-a putut actualiza clubul");
    }
  };

  const handleDeleteClub = async () => {
    if (!selectedClub) return;
    try {
      await deleteClub(selectedClub.id);
      toast.success("Club șters cu succes!");
      setShowDeleteModal(false);
      setSelectedClub(null);
      fetchClubs();
    } catch (error: any) {
      console.error("Failed to delete club:", error);
      toast.error(error.response?.data?.error || "Nu s-a putut șterge clubul");
    }
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClub) return;
    try {
      await addClubMember(selectedClub.id, memberFormData);
      toast.success("Membru adăugat cu succes!");
      setShowAddMemberModal(false);
      resetMemberForm();
      fetchClubMembers(selectedClub.id);
      fetchClubs(); // Refresh club list to update member counts
    } catch (error: any) {
      console.error("Failed to add member:", error);
      toast.error(error.response?.data?.error || "Nu s-a putut adăuga membrul");
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!selectedClub) return;
    if (!window.confirm("Sigur vrei să elimini acest membru?")) return;
    try {
      await removeClubMember(selectedClub.id, userId);
      toast.success("Membru eliminat cu succes!");
      fetchClubMembers(selectedClub.id);
      fetchClubs(); // Refresh club list to update member counts
    } catch (error: any) {
      console.error("Failed to remove member:", error);
      toast.error(error.response?.data?.error || "Nu s-a putut elimina membrul");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      logo_url: "",
      description: "",
      physical_address: "",
      plan_code: "demo",
    });
  };

  const resetMemberForm = () => {
    setMemberFormData({
      user_id: "",
      role_code: "athlete",
    });
  };

  const openEditModal = (club: Club) => {
    setSelectedClub(club);
    setFormData({
      name: club.name,
      logo_url: club.logo_url || "",
      description: club.description || "",
      physical_address: club.physical_address || "",
      plan_code: club.plan_code,
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (club: Club) => {
    setSelectedClub(club);
    setShowDeleteModal(true);
  };

  const openMembersModal = async (club: Club) => {
    setSelectedClub(club);
    await fetchClubMembers(club.id);
    setShowMembersModal(true);
  };

  const openAddMemberModal = async () => {
    await fetchAvailableUsers();
    resetMemberForm();
    setShowAddMemberModal(true);
  };

  const getStatusBadge = (clubAdmin?: ClubAdmin | null) => {
    if (!clubAdmin) {
      return (
        <span className="px-2 py-1 bg-slate-100 text-slate-500 rounded-lg text-xs font-medium cursor-default">
          Fără admin
        </span>
      );
    }

    if (clubAdmin.status === "active") {
      return (
        <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-semibold cursor-default">
          Activ
        </span>
      );
    }
    if (clubAdmin.status === "demo") {
      const daysLeft = clubAdmin.demo_expires_at
        ? Math.max(0, Math.ceil((new Date(clubAdmin.demo_expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
        : 0;
      const tooltipText = clubAdmin.demo_started_at && clubAdmin.demo_expires_at
        ? `Demo început: ${formatDate(new Date(clubAdmin.demo_started_at))}\nExpiră: ${formatDate(new Date(clubAdmin.demo_expires_at))}\nTimp rămas: ${daysLeft} zile`
        : clubAdmin.demo_expires_at
        ? `Expiră: ${formatDate(new Date(clubAdmin.demo_expires_at))}\nTimp rămas: ${daysLeft} zile`
        : `Timp rămas: ${daysLeft} zile`;
      return (
        <span
          className={`px-2 py-1 rounded-lg text-xs font-semibold cursor-help ${daysLeft <= 3 ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"}`}
          title={tooltipText}
        >
          Demo ({daysLeft}z)
        </span>
      );
    }
    return (
      <span className="px-2 py-1 bg-red-50 text-red-600 rounded-lg text-xs font-semibold cursor-default">
        Inactiv
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-[1500px]">
        {/* Header */}
        <AdminPageHeader
          eyebrow="Platformă"
          title={t("admin.clubManagement.title", "Gestionare Cluburi")}
          subtitle={t("admin.clubManagement.description", "Gestionează cluburile și membrii acestora")}
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
          actions={
            <AdminButton
              onClick={() => setShowCreateModal(true)}
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
            >
              {t("admin.clubManagement.createClub", "Creează Club")}
            </AdminButton>
          }
        />

        {/* Actions */}
        <div className="mb-6 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
          {/* Search Bar */}
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("admin.clubManagement.searchPlaceholder", "Caută cluburi...")}
              className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Filters. Only the ones that can return something: the options are
              built from the clubs actually present. */}
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            className="px-3 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t("admin.clubManagement.allCities", "Toate orașele")}</option>
            {cities.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="px-3 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t("admin.clubManagement.allPlans", "Toate planurile")}</option>
            {definedPlans.map((p) => (
              <option key={p.code} value={p.code}>{p.name}</option>
            ))}
            {legacyPlanCodes.length > 0 && (
              <optgroup label={t("admin.clubManagement.legacyPlans", "Planuri vechi, încă folosite")}>
                {legacyPlanCodes.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </optgroup>
            )}
          </select>
          <select
            value={adminFilter}
            onChange={(e) => setAdminFilter(e.target.value as "" | "with" | "without")}
            className="px-3 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t("admin.clubManagement.adminAny", "Cu sau fără admin")}</option>
            <option value="with">{t("admin.clubManagement.adminWith", "Au admin de club")}</option>
            <option value="without">{t("admin.clubManagement.adminWithout", "Fără admin de club")}</option>
          </select>

          <div className="flex items-center gap-2 ml-auto">
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-800 whitespace-nowrap"
              >
                {t("common.clearFilters", "Resetează filtrele")}
              </button>
            )}
            <span className="text-sm text-slate-500 whitespace-nowrap">
              {filteredClubs.length}
              {filteredClubs.length !== clubs.length ? ` / ${clubs.length}` : ""}
            </span>
            <div className="flex gap-1 p-1 border rounded-xl border-slate-200 bg-slate-100">
              {([["table", t("admin.clubManagement.viewTable", "Tabel")], ["cards", t("admin.clubManagement.viewCards", "Carduri")]] as const).map(
                ([id, label]) => (
                  <button
                    key={id}
                    onClick={() => setView(id)}
                    className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
                      view === id ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {label}
                  </button>
                )
              )}
            </div>
          </div>
        </div>

        {/* Clubs Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-slate-600 font-medium">{t("common.loading", "Se încarcă...")}</span>
            </div>
          </div>
        ) : clubs.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
            <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <p className="text-slate-500">{t("admin.clubManagement.noClubs", "Niciun club găsit")}</p>
          </div>
        ) : filteredClubs.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
            <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <p className="text-slate-500 mb-2">{t("admin.clubManagement.noResults", "Niciun club nu corespunde căutării")}</p>
            <button
              onClick={() => setSearchTerm("")}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              {t("common.clearSearch", "Șterge căutarea")}
            </button>
          </div>
        ) : view === "table" ? (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-slate-200 bg-slate-50">
                    {(
                      [
                        ["name", t("admin.clubManagement.club", "Club"), "left"],
                        ["admin", t("admin.clubManagement.admin", "Admin club"), "left"],
                        ["coaches", t("admin.clubManagement.coaches", "Antrenori"), "right"],
                        ["athletes", t("admin.clubManagement.athletes", "Sportivi"), "right"],
                        ["sports", t("admin.clubManagement.sports", "Sporturi"), "right"],
                        ["plan", t("admin.clubManagement.plan", "Plan"), "left"],
                      ] as const
                    ).map(([col, label, align]) => {
                      // The admin column is a yes/no, not a scale — sorting by
                      // it is what the "fără admin" filter is for.
                      const sortable = col !== "admin";
                      const active = sortBy === (col as any);
                      return (
                        <th
                          key={col}
                          onClick={sortable ? () => toggleSort(col as any) : undefined}
                          className={`px-4 py-3 font-semibold text-slate-600 whitespace-nowrap ${
                            align === "right" ? "text-right" : "text-left"
                          } ${sortable ? "cursor-pointer select-none hover:text-slate-900" : ""}`}
                        >
                          {label}
                          {active && <span className="ml-1 text-slate-400">{sortDir === "asc" ? "▲" : "▼"}</span>}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {pagedClubs.map(({ club }) => {
                    const admin = adminOf(club);
                    return (
                      <tr key={club.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 group">
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-3 min-w-0">
                            {club.logo_url ? (
                              <img src={club.logo_url} alt="" className="flex-shrink-0 object-cover w-8 h-8 rounded-lg" />
                            ) : (
                              <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 text-xs font-bold text-blue-600 rounded-lg bg-blue-50">
                                {club.name.substring(0, 2).toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0">
                              <button
                                onClick={() => navigate(`/admin/clubs/${club.id}`)}
                                className="font-medium text-left truncate text-slate-800 hover:text-blue-600"
                              >
                                {club.name}
                              </button>
                              <span className="block text-[11px] text-slate-500">
                                {club.city || "—"}
                                {club.status && club.status !== "active" && (
                                  <span className="ml-1 font-semibold text-rose-600">· {club.status}</span>
                                )}
                              </span>
                              {/* Row actions, kept out of the way until the
                                  row is pointed at: they are rare next to
                                  "open the club", and a column of buttons on
                                  every row is what made this list heavy.
                                  Focus-within keeps them reachable by keyboard. */}
                              <div className="flex items-center gap-2 mt-1 transition-opacity opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                                <button
                                  onClick={() => openEditModal(club)}
                                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  {t("common.edit", "Editează")}
                                </button>
                                <button
                                  onClick={() => openMembersModal(club)}
                                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-emerald-700"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  {t("admin.clubManagement.members", "Membri")}
                                </button>
                                <button
                                  onClick={() => openDeleteModal(club)}
                                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-red-600"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  {t("common.delete", "Șterge")}
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {admin ? (
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="min-w-0">
                                {/* Straight to the person: their profile is
                                    where you suspend them, resend their setup
                                    mail or read their roles. */}
                                <button
                                  onClick={() => navigate(`/admin/users/${admin.id}`)}
                                  className="block text-left truncate text-slate-700 hover:text-blue-600 max-w-[200px]"
                                >
                                  {admin.full_name || admin.email}
                                </button>
                                {admin.status && admin.status !== "active" && (
                                  <span className="block text-[11px] text-amber-600">{admin.status}</span>
                                )}
                              </div>
                              <LoginAsButton userId={admin.id} label="Login as" compact />
                            </div>
                          ) : (
                            // The thing worth spotting in this list: a club
                            // nobody administers cannot do anything at all.
                            <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-bold rounded-full bg-rose-100 text-rose-700">
                              {t("admin.clubManagement.noAdmin", "lipsă")}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-700">{club.coach_count ?? 0}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-700">{club.member_count ?? 0}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-700">{club.sports_count ?? 0}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold capitalize rounded-full bg-slate-100 text-slate-700">
                            {club.plan_code}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pagedClubs.map(({ club, role }) => (
              <div
                key={club.id}
                className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-lg hover:border-blue-200 transition-all duration-300 group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-800 mb-1 group-hover:text-blue-600 transition-colors">{club.name}</h3>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-slate-500 uppercase font-medium px-2 py-1 bg-slate-100 rounded-lg">{role}</span>
                      {getStatusBadge((club as any).club_admin)}
                    </div>
                  </div>
                  {club.logo_url && (
                    <img
                      src={club.logo_url}
                      alt={club.name}
                      className="w-14 h-14 rounded-xl object-cover ml-4 border border-slate-200"
                    />
                  )}
                </div>

                {club.description && (
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">{club.description}</p>
                )}

                {club.physical_address && (
                  <p className="text-xs text-slate-500 mb-4 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {club.physical_address}
                  </p>
                )}

                <div className="flex items-center justify-between text-sm text-slate-500 mb-4 py-3 border-t border-b border-slate-100">
                  <span className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                    <span className="capitalize">{club.plan_code}</span>
                  </span>
                  {club.member_count !== undefined && (
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      {club.member_count} {t("admin.clubManagement.members", "membri")}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => navigate(`/admin/clubs/${club.id}`)}
                    className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
                  >
                    {t("common.view", "Vezi")} {t("admin.clubManagement.details", "Detalii")}
                  </button>
                  {/* Per-club "Login as" — borrows the session of the
                      club's admin (the user surfaced under
                      club.club_admin from GET /clubs). Skipped silently
                      if the club has no club_admin attached. */}
                  {(club as any).club_admin?.id && (
                    <LoginAsButton
                      userId={(club as any).club_admin.id}
                      label={`Autentificare ca ${(club as any).club_admin.full_name || (club as any).club_admin.email}`}
                      className="w-full justify-center"
                    />
                  )}
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => openEditModal(club)}
                      className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-medium transition-all duration-200"
                    >
                      {t("common.edit", "Editează")}
                    </button>
                    <button
                      onClick={() => openMembersModal(club)}
                      className="px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl text-sm font-medium transition-all duration-200"
                    >
                      {t("admin.clubManagement.members", "Membri")}
                    </button>
                    <button
                      onClick={() => openDeleteModal(club)}
                      className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-sm font-medium transition-all duration-200"
                    >
                      {t("common.delete", "Șterge")}
                    </button>
                  </div>
                </div>
              </div>
            ))}
            <div className="md:col-span-2 lg:col-span-3 bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
            </div>
          </div>
        )}

        {/* Create Club Modal */}
        {showCreateModal && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-fast"
            onClick={() => { setShowCreateModal(false); resetForm(); }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto border border-slate-200 animate-modal-in"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-bold text-slate-800 mb-6">
                {t("admin.clubManagement.createClub", "Creează Club")}
              </h2>
              <form onSubmit={handleCreateClub}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("admin.clubManagement.name", "Nume Club")} *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                  <ImageUpload
                    value={formData.logo_url}
                    onChange={(url) => setFormData({ ...formData, logo_url: url })}
                    endpoint="club-logo"
                    label={t("admin.clubManagement.logoUrl", "Logo Club")}
                    maxSizeMB={5}
                    previewClassName="w-32 h-32"
                  />
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("admin.clubManagement.description", "Descriere")}
                    </label>
                    <textarea
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("admin.clubManagement.address", "Adresă")}
                    </label>
                    <input
                      type="text"
                      value={formData.physical_address}
                      onChange={(e) => setFormData({ ...formData, physical_address: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("admin.clubManagement.plan", "Plan")}
                    </label>
                    <select
                      value={formData.plan_code}
                      onChange={(e) => setFormData({ ...formData, plan_code: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="demo">Demo (trial)</option>
                      <option value="basic">Basic (1 EUR/sportiv/luna)</option>
                      <option value="premium">Premium (2 EUR/sportiv/luna)</option>
                    </select>
                  </div>
                </div>
                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all duration-200 font-medium"
                  >
                    {t("common.cancel", "Anulează")}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-medium shadow-lg shadow-blue-500/25"
                  >
                    {t("common.create", "Creează")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Club Modal */}
        {showEditModal && selectedClub && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-fast"
            onClick={() => { setShowEditModal(false); setSelectedClub(null); resetForm(); }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto border border-slate-200 animate-modal-in"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-bold text-slate-800 mb-6">
                {t("admin.clubManagement.editClub", "Editează Club")}
              </h2>
              <form onSubmit={handleUpdateClub}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("admin.clubManagement.name", "Nume Club")} *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                  <ImageUpload
                    value={formData.logo_url}
                    onChange={(url) => setFormData({ ...formData, logo_url: url })}
                    endpoint="club-logo"
                    label={t("admin.clubManagement.logoUrl", "Logo Club")}
                    maxSizeMB={5}
                    previewClassName="w-32 h-32"
                  />
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("admin.clubManagement.description", "Descriere")}
                    </label>
                    <textarea
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("admin.clubManagement.address", "Adresă")}
                    </label>
                    <input
                      type="text"
                      value={formData.physical_address}
                      onChange={(e) => setFormData({ ...formData, physical_address: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("admin.clubManagement.plan", "Plan")}
                    </label>
                    <select
                      value={formData.plan_code}
                      onChange={(e) => setFormData({ ...formData, plan_code: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="demo">Demo (trial)</option>
                      <option value="basic">Basic (1 EUR/sportiv/luna)</option>
                      <option value="premium">Premium (2 EUR/sportiv/luna)</option>
                    </select>
                  </div>
                </div>
                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedClub(null);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all duration-200 font-medium"
                  >
                    {t("common.cancel", "Anulează")}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-medium shadow-lg shadow-blue-500/25"
                  >
                    {t("common.save", "Salvează")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedClub && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-fast"
            onClick={() => { setShowDeleteModal(false); setSelectedClub(null); }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md border border-red-200 animate-modal-in"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-slate-800">
                  {t("admin.clubManagement.deleteClub", "Șterge Club")}
                </h2>
              </div>
              <p className="text-slate-600 mb-6">
                {t("admin.clubManagement.deleteConfirm", "Sigur vrei să ștergi")}{" "}
                <strong className="text-slate-800">{selectedClub.name}</strong>?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedClub(null);
                  }}
                  className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all duration-200 font-medium"
                >
                  {t("common.cancel", "Anulează")}
                </button>
                <button
                  onClick={handleDeleteClub}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-200 font-medium shadow-lg shadow-red-500/25"
                >
                  {t("common.delete", "Șterge")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Members Modal */}
        {showMembersModal && selectedClub && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-fast"
            onClick={() => { setShowMembersModal(false); setSelectedClub(null); setClubMembers([]); }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-slate-200 animate-modal-in"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-800">
                  {t("admin.clubManagement.clubMembers", "Membrii Clubului")} - {selectedClub.name}
                </h2>
                <button
                  onClick={openAddMemberModal}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition-all duration-200 shadow-lg shadow-blue-500/25 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t("admin.clubManagement.addMember", "Adaugă Membru")}
                </button>
              </div>

              {clubMembers.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p className="text-slate-500">
                    {t("admin.clubManagement.noMembers", "Niciun membru încă")}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {clubMembers.map((member) => (
                    <div
                      key={member.user.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-slate-300 transition-all duration-200"
                    >
                      <div className="flex-1">
                        <div className="text-slate-800 font-medium">
                          {member.user.full_name || member.user.email}
                        </div>
                        <div className="text-sm text-slate-500">{member.user.email}</div>
                        <div className="text-xs text-blue-600 font-medium mt-1 px-2 py-0.5 bg-blue-50 rounded-lg inline-block">
                          {member.role.label} ({member.role.code})
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveMember(member.user.id)}
                        className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-sm font-medium transition-all duration-200"
                      >
                        {t("common.remove", "Elimină")}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6">
                <button
                  onClick={() => {
                    setShowMembersModal(false);
                    setSelectedClub(null);
                    setClubMembers([]);
                  }}
                  className="w-full px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all duration-200 font-medium"
                >
                  {t("common.close", "Închide")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Member Modal */}
        {showAddMemberModal && selectedClub && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-fast"
            onClick={() => { setShowAddMemberModal(false); resetMemberForm(); }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md border border-slate-200 animate-modal-in"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-bold text-slate-800 mb-6">
                {t("admin.clubManagement.addMember", "Adaugă Membru")}
              </h2>
              <form onSubmit={handleAddMember}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("admin.clubManagement.selectUser", "Selectează Utilizator")} *
                    </label>
                    <select
                      required
                      value={memberFormData.user_id}
                      onChange={(e) => setMemberFormData({ ...memberFormData, user_id: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="">
                        {t("admin.clubManagement.chooseUser", "Alege un utilizator...")}
                      </option>
                      {availableUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.full_name || user.email} ({user.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("admin.clubManagement.role", "Rol")} *
                    </label>
                    <select
                      value={memberFormData.role_code}
                      onChange={(e) => setMemberFormData({ ...memberFormData, role_code: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="club_admin">Admin Club</option>
                      <option value="coach">Antrenor</option>
                      <option value="athlete">Sportiv</option>
                    </select>
                  </div>
                </div>
                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddMemberModal(false);
                      resetMemberForm();
                    }}
                    className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all duration-200 font-medium"
                  >
                    {t("common.cancel", "Anulează")}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-medium shadow-lg shadow-blue-500/25"
                  >
                    {t("common.add", "Adaugă")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
